import React, { useContext, useMemo, useState } from 'react';
import styles from './PatientsPreconfigCheckList.module.css';

import { getFullName } from '../../../services/getPatientAtribute';

import PatientsContext from '../../../context/PatientsContext';

import Searchbar from '../../UI/Searchbar/Searchbar';
import CheckboxButton from '../../UI/CheckboxButton/CheckboxButton';

import { Patient } from '../../../interfaces/Patient';

import { useTranslation } from 'react-i18next';

type PatientCase = NonNullable<Patient['cases']>[number];

interface CaseRow {
    patient: Patient
    caseItem: PatientCase
}

interface Props {
    caseIdsLinked?: number[]
    caseIdsBlocked?: number[]
    caseIdsHidden?: number[]
    caseIdsToShow?: number[]
    searchbar: boolean
    handleClickCheckbox: (checked: boolean, caseId: number) => void
}

const PatientsPreconfigCheckList: React.FC<Props> = ({
    caseIdsLinked = [],
    caseIdsBlocked = [],
    caseIdsHidden = [],
    caseIdsToShow,
    searchbar,
    handleClickCheckbox
}) => {
    const { t } = useTranslation();
    const patientsContext = useContext(PatientsContext);

    const [searchbarText, setSearchbarText] = useState('');

    const linkedSet = useMemo(() => new Set(caseIdsLinked), [caseIdsLinked]);
    const blockedSet = useMemo(() => new Set(caseIdsBlocked), [caseIdsBlocked]);
    const hiddenSet = useMemo(() => new Set(caseIdsHidden), [caseIdsHidden]);
    const toShowSet = useMemo(() => caseIdsToShow ? new Set(caseIdsToShow) : null, [caseIdsToShow]);

    const allCases = useMemo<CaseRow[]>(() => {
        return patientsContext.patients.flatMap((patient) =>
            (patient.cases ?? []).map((caseItem) => ({
                patient,
                caseItem,
            }))
        );
    }, [patientsContext.patients]);

    const casesFiltered = useMemo(() => {
        const search = searchbarText.toLowerCase();

        const filteredByShow = toShowSet
            ? allCases.filter(({ caseItem }) => toShowSet.has(caseItem.id))
            : allCases;

        const filteredByHidden = filteredByShow.filter(({ caseItem }) => !hiddenSet.has(caseItem.id));

        return filteredByHidden.filter(({ patient, caseItem }) =>
            getFullName(patient).toLowerCase().includes(search) ||
            (patient.mrn && patient.mrn.toString().includes(searchbarText)) ||
            (caseItem.hospital_case && caseItem.hospital_case.toLowerCase().includes(search))
        );
    }, [allCases, hiddenSet, searchbarText, toShowSet]);

    const handleChangeSearchbar = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setSearchbarText(evt.target.value);
    };

    return (
        <>
            {searchbar && (
                <div className={styles.searchbarContainer}>
                    <Searchbar
                        handleChange={handleChangeSearchbar}
                        placeholder={t('search_by_name_or_NHC') as string}
                    />
                </div>
            )}

            <div className={styles.patientsChecklistContainer}>
                {casesFiltered.map(({ patient, caseItem }) => (
                    <CheckboxButton
                        key={caseItem.id}
                        checked={linkedSet.has(caseItem.id)}
                        onClick={(checked: boolean) => handleClickCheckbox(checked, caseItem.id)}
                        label={`${patient.mrn ?? ''} - ${getFullName(patient)} - ${caseItem.hospital_case ?? `Case ${caseItem.id}`}`}
                        disabled={blockedSet.has(caseItem.id)}
                    />
                ))}
            </div>
        </>
    );
};

export default PatientsPreconfigCheckList;